import '@naturehouse/design-system/components/protons/icon/Icon';
import CardHouseTemplate from '@templates/components/organisms/card-house.html.njk';
import type ToggleIconButton from '@naturehouse/design-system/components/molecules/toggle-icon-button/ToggleIconButton';
import type {
    Observer,
    Subject
} from '@naturehouse/nh-essentials/lib/architecture/ObserverPattern';
import parseStringAsHtml from '@naturehouse/nh-essentials/lib/html/parser';
import {
    FORMATS,
    intlDateFormat
} from '@naturehouse/nh-essentials/lib/dates/intlDateTimeFormatter';
import SearchClickEventTargetManager from '../../../common/search/SearchClickEventTargetManager';
import ApiRequestState from '../../../common/search/store/ApiRequestState';
import { FeatureToggle, isFeatureEnabled } from '../../../util/featureToggle';
import ArrivalDepartureFilter from '../store/ArrivalDepartureFilter';
import FavoritesStore from '../store/FavoritesStore';
import type { HousePrice } from '../store/HousePrices';
import HousePrices from '../store/HousePrices';
import HouseSearchFilters from '../store/HouseSearchFilters';
import HouseSearchListItems from '../store/HouseSearchListItems';
import HouseSearchListItemsFactory from '../store/HouseSearchListItemsFactory';
import ImageCarouselTrackingManager from '../ImageCarouselTrackingManager';
import priceRenderer from './PriceRenderer';
import SearchResultsExtraCostsDialogHandler from './SearchResultsExtraCostsDialogHandler';

type SearchResultsBinding = {
    houses: HouseSearchListItem[];
    results: string;
    max: string;
    totalResults: number;
};

export interface SearchResultsContextInterface extends ContextInterface {
    element: HTMLElement;
    bindings: SearchResultsBinding;
}

export default class SearchResults implements Observer {
    private readonly context: SearchResultsContextInterface;

    private readonly favoriteStore: typeof FavoritesStore = FavoritesStore;

    readonly #apiRequestState = ApiRequestState.getInstance();

    readonly #searchListItems;

    readonly #housePrices = HousePrices.getInstance();

    readonly #arrivalDepartureFilter = HouseSearchFilters.getInstance().get(
        ArrivalDepartureFilter.NAME
    );

    readonly #translations: Record<string, string> = {};

    readonly #clickEventTargetManager: SearchClickEventTargetManager | null = null;

    readonly #extraCostsDialogHandler: SearchResultsExtraCostsDialogHandler =
        SearchResultsExtraCostsDialogHandler.getInstance();

    readonly #isSearchLowestPriceVariantActive = isFeatureEnabled(
        FeatureToggle.SEARCH_LOWEST_PRICE
    );

    readonly #isGeneralScoreFiveScaleEnabled = isFeatureEnabled(
        FeatureToggle.GENERAL_SCORE_FIVE_SCALE
    );

    constructor(context: SearchResultsContextInterface) {
        this.context = context;
        this.context.bindings.houses = [];

        const initialHouses: SearchResponseHouse[] = JSON.parse(this.context.bindings.results);
        const totalResults = Number(this.context.bindings.totalResults);
        const houses = HouseSearchListItemsFactory.fromArray(initialHouses);
        this.#searchListItems = HouseSearchListItems.getInstance();
        this.#searchListItems.setResults(houses, totalResults);

        const list: HTMLElement | null =
            this.context.element.querySelector('[data-role="list-view"]');

        if (!list) {
            return;
        }

        this.#translations = JSON.parse(list.dataset.translations ?? '{}');
        this.#clickEventTargetManager = new SearchClickEventTargetManager('open-house-detail');
    }

    update(subject: Subject): void {
        if (subject instanceof ApiRequestState) {
            this.#handleApiRequestState(subject);
            return;
        }

        if (subject instanceof HouseSearchListItems) {
            this.#handleSearchResults(subject);
        }

        if (subject instanceof HousePrices) {
            subject.prices.forEach((price) => this.#renderPricing(price));
        }
    }

    onInit(): void {
        this.#searchListItems.attach(this);
        this.#apiRequestState.attach(this);
        this.#housePrices.attach(this);

        this.favoriteStore.getFavorites().then(() => {
            this.setHouseFavoriteState();
        });

        this.setEventListeners();
        this.#showHidePriceDataMessage();
    }

    private setEventListeners(): void {
        const results: HTMLElement[] = this.getSearchResults();
        results.forEach((result: HTMLElement) => {
            const card: HTMLElement | null = result.querySelector('article[data-house-id]');
            if (!card) {
                return;
            }

            const id = card.dataset.houseId;
            this.#handleImageCarouselTracking(result, id ?? 'unknown');
        });

        this.#extraCostsDialogHandler.setEventListeners();
    }

    private setHouseFavoriteState(): void {
        const favoriteButtons: ToggleIconButton[] = Array.from(
            this.context.element.querySelectorAll('[data-role="favoriteButton"]')
        );

        if (!favoriteButtons) {
            return;
        }

        for (let index = 0; index < favoriteButtons.length; index++) {
            const favoriteButton = favoriteButtons[index];
            const card: HTMLElement | null = favoriteButton.closest('[data-house-id]');
            const houseId: number | null = Number(card?.dataset.houseId) || null;

            try {
                if (houseId === null || !this.favoriteStore.state.houseIds.includes(houseId)) {
                    continue;
                }
            } catch (error) {
                continue;
            }

            favoriteButton.active = true;
            favoriteButton.dataset.bindFavorited = 'true';
            favoriteButton.classList.remove('disabled');
        }
    }

    private clearSearchResults(): void {
        const results: HTMLElement[] = this.getSearchResults();
        results.forEach((result) => {
            result.remove();
        });
    }

    #clearNotifications(): void {
        const notifications: NodeListOf<HTMLElement> = this.context.element.querySelectorAll(
            'li[data-type="notification"]'
        );

        notifications.forEach((notification) => {
            notification.remove();
        });
    }

    private getSearchResults(): HTMLElement[] {
        return Array.from(this.context.element.querySelectorAll('.search__result-list__item'));
    }

    #renderPricingSpinner(house: HouseSearchListItem): void {
        const pricingElement = this.context.element.querySelector(
            `[data-house-id="${house.id}"] [data-section="pricing"]`
        ) as HTMLTemplateElement;

        if (pricingElement === null) {
            return;
        }

        pricingElement.innerHTML = `
            <div class="search__result__price-loader">
                <div class="loading-spinner"></div>
            </div>
        `;
    }

    #renderPricing(housePrice: HousePrice): void {
        const pricingElement = this.context.element.querySelector(
            `[data-house-id="${housePrice.id}"] [data-section="pricing"]`
        ) as HTMLTemplateElement;

        if (pricingElement === null) {
            return;
        }

        if (housePrice.price === null) {
            pricingElement.innerHTML = '';
            return;
        }

        priceRenderer.render(pricingElement, housePrice, this.#translations);
    }

    #handleApiRequestState(state: ApiRequestState): void {
        const list: HTMLElement | null =
            this.context.element.querySelector('[data-role="list-view"]');

        const target = list ?? this.context.element;

        if (state.state === ApiRequestState.REQUESTING) {
            target.classList.add('skeleton-loader');
            return;
        }

        if (
            [ApiRequestState.REQUEST_SUCCESS, ApiRequestState.REQUEST_FAILED].includes(state.state)
        ) {
            target.classList.remove('skeleton-loader');
        }

        this.#toggleTimeoutNotification(state.state === ApiRequestState.REQUEST_FAILED);
    }

    #renderHouses(
        housesWithinSelection: HouseSearchListItem[],
        userHasPeriodSelected: boolean
    ): void {
        this.clearSearchResults();

        const list: HTMLElement | null =
            this.context.element.querySelector('[data-role="list-view"]');

        if (!list) {
            return;
        }

        const toAppendArray: HTMLElement[] = [];
        for (let index = 0; index < housesWithinSelection.length; index++) {
            toAppendArray.push(
                this.#renderHouse(housesWithinSelection[index], list, userHasPeriodSelected)
            );
        }
        list.innerHTML = '';
        list.append(...toAppendArray);

        if (this.#clickEventTargetManager === null) {
            return;
        }
        this.#clickEventTargetManager.targetElement = list;
    }

    #renderHouse(
        house: HouseSearchListItem,
        list: HTMLElement,
        userHasPeriodSelected: boolean
    ): HTMLElement {
        const listItem = document.createElement('li');
        listItem.dataset.testid = 'house-card';
        listItem.classList.add('search__result-list__item');

        const translations: Record<string, string> = JSON.parse(list.dataset.translations ?? '{}');
        const houseWithFiveScaleRating = this.#isGeneralScoreFiveScaleEnabled
            ? {
                  ...house,
                  houseRating:
                      house.houseRating !== null
                          ? Math.ceil((house.houseRating / 2) * 10) / 10
                          : null
              }
            : house;

        const renderedTemplate = CardHouseTemplate.render({
            house: houseWithFiveScaleRating,
            compact: list.dataset.compact === 'true',
            translations: translations,
            priceStartDate: house.price?.startDateFormatted ? house.price.startDateFormatted : '',
            arrivalDate: house.price?.startDate
                ? intlDateFormat(house.price?.startDate, FORMATS.SHORT_WITH_WEEKDAY)
                : null,
            departureDate: house.price?.endDate
                ? intlDateFormat(house.price?.endDate, FORMATS.SHORT_WITH_WEEKDAY)
                : null,
            isSearchLowestPriceVariantActive: this.#isSearchLowestPriceVariantActive,
            isGeneralScoreFiveScaleEnabled: this.#isGeneralScoreFiveScaleEnabled,
            userHasPeriodSelected: userHasPeriodSelected,
            isPriceExcludingTouristTax: house.isPriceExcludingTouristTax ?? false
        });

        const article = parseStringAsHtml(renderedTemplate, 'article');
        listItem.appendChild(article);

        this.#handleImageCarouselTracking(listItem, house.id.toString());

        return listItem;
    }

    #handleImageCarouselTracking(container: HTMLElement, houseId: string): void {
        const imageCarousel = container.querySelector<HTMLElement>('[data-role="simple-carousel"]');

        if (!imageCarousel) {
            return;
        }

        ImageCarouselTrackingManager.getInstance(imageCarousel, houseId);
    }

    #handleSearchResults(list: HouseSearchListItems): void {
        this.#clearNotifications();
        this.clearSearchResults();
        this.#extraCostsDialogHandler.removeEventListeners();
        this.#showHidePriceDataMessage();

        this.#renderHouses(list.houses, list.userHasPeriodSelected);

        this.context.bindings.houses = this.context.bindings.max
            ? list.houses.slice(0, Number(this.context.bindings.max))
            : list.houses;

        const counterElement: HTMLElement | null = this.context.element.querySelector(
            '[data-search-result-counter]'
        );

        const resultsNotificationElement: HTMLElement | null = this.context.element.querySelector(
            '[data-name="search-result-notification"]'
        );

        const noResultsElement: HTMLElement | null = this.context.element.querySelector(
            '[data-search-no-results]'
        );

        if (resultsNotificationElement !== null) {
            resultsNotificationElement.classList.toggle('hidden', list.total > 10);
        }

        if (noResultsElement !== null) {
            noResultsElement.classList.toggle('hidden', list.total > 0);
        }

        if (counterElement !== null) {
            counterElement.innerText = String(list.total);
        }

        this.favoriteStore.getFavorites().then(() => {
            this.setHouseFavoriteState();
        });

        const asyncPricedHouses = this.#searchListItems.houses.filter((house) => house.externalId);

        if (asyncPricedHouses.length) {
            asyncPricedHouses.forEach(this.#renderPricingSpinner.bind(this));
        }

        this.setEventListeners();
    }

    #showHidePriceDataMessage(): void {
        const priceDateMessage: HTMLElement | null = document.querySelector(
            '[data-role="price-date-message"]'
        );

        if (priceDateMessage) {
            const arrivalDate = this.#arrivalDepartureFilter?.parameters.arrivalDate;
            priceDateMessage.hidden = typeof arrivalDate === 'string';
        }
    }

    #toggleTimeoutNotification(show: boolean): void {
        const timeOutNotification = document.querySelector(
            '[data-name="search-timeout-notification"]'
        );

        if (!timeOutNotification) {
            return;
        }

        timeOutNotification.classList.toggle('hidden', !show);
    }

    static getClassName(): string {
        return 'SearchResults';
    }
}
